// Globals
import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'antd/lib/form/Form';

// Modules
import { useAutocompleteSettlements, useAutocompleteWarehouses } from '@/modules/Regions/hooks';
import { buildSettlementOptions } from '@/modules/Regions/helpers';
import { MapsProvider, PlaceAutocomplete } from '@/modules/Maps';

// Models
import { BuildingTypes, IOrderDetails, OrdersDeliveryType } from '@/models/orders.model';

// Components
import {
  StyledTitleModal,
  StyledButton,
  StyledForm,
  StyledFormItem,
  StyledSelect,
  StyledTextArea,
  StyledFormItemWrap,
  StyledInputNumber,
  StyledInput,
} from './UpdateOrderDeliveryModalStyles';
import { Modal, ModalSize, IFormInstance } from '@/components';

// Helpers
import {
  buildingTypeOptions,
  elevatorOptions,
  getDeliveryTypeOptions,
  getDepartmentName,
  getDepartments,
  TUpdateOrderDeliveryFormValues,
  UpdateOrderDeliveryFormFieldsNames,
  useUpdateOrderDeliveryFormRules,
} from '../../helpers';
import { useAutocompleteNovaPoshtaDepartments } from '../../hooks';

interface UpdateOrderDeliveryModalProps {
  handleSubmit: (values: TUpdateOrderDeliveryFormValues) => void;
  formInstance: IFormInstance;
  initialValues?: TUpdateOrderDeliveryFormValues;
  isOpenUpdateOrderDeliveryModal: boolean;
  order: IOrderDetails;
  closeUpdateOrderDeliveryModal: () => void;
  setRegionId: Dispatch<SetStateAction<string | null>>;
}

const UpdateOrderDeliveryModal = ({
  isOpenUpdateOrderDeliveryModal,
  closeUpdateOrderDeliveryModal,
  order,
  formInstance,
  initialValues,
  handleSubmit,
  setRegionId,
}: UpdateOrderDeliveryModalProps) => {
  const { t } = useTranslation(['orders']);
  const validationRules = useUpdateOrderDeliveryFormRules(t);
  const delivery = order.delivery;

  const deliveryType = useWatch(UpdateOrderDeliveryFormFieldsNames.DELIVERY_TYPE, formInstance);
  const buildingType = useWatch(UpdateOrderDeliveryFormFieldsNames.BUILDING_TYPE, formInstance);
  const selectedSettlement = useWatch(UpdateOrderDeliveryFormFieldsNames.SETTLEMENT, formInstance);
  const deliveryDepartment = useWatch(UpdateOrderDeliveryFormFieldsNames.DELIVERY_DEPARTMENT, formInstance);
  const resolvedDeliveryType = deliveryType ?? delivery?.delivery;
  const isPickup = resolvedDeliveryType === OrdersDeliveryType.PICKUP;

  const { isLoading, onSearch, settlementsOptions } = useAutocompleteSettlements(isPickup ? '' : delivery?.settlement, {
    withPickup: isPickup,
  });
  const {
    isLoading: isWarehousesLoading,
    onSearch: onWarehousesSearch,
    warehousesOptions,
  } = useAutocompleteWarehouses(isPickup ? selectedSettlement : null);

  const {
    isLoading: isDepartmentsLoading,
    onSearch: onDepartmentsSearch,
    departmentsOptions,
  } = useAutocompleteNovaPoshtaDepartments(order.id);

  const settlements = (() => {
    if (!isPickup) {
      return buildSettlementOptions({
        settlementOptions: settlementsOptions || [],
        label: delivery?.settlement ?? '',
        value: delivery?.settlement_id ?? '',
      });
    }

    const pickupOptions = settlementsOptions || [];
    const hasSelectedPickupCity = pickupOptions.some((option) => option.value === selectedSettlement);

    if (
      selectedSettlement &&
      !hasSelectedPickupCity &&
      delivery?.settlement_id === selectedSettlement &&
      delivery?.settlement
    ) {
      return buildSettlementOptions({
        settlementOptions: pickupOptions,
        label: delivery.settlement,
        value: delivery.settlement_id,
      });
    }

    return pickupOptions;
  })();
  const departments = getDepartments(departmentsOptions ?? [], {
    label: getDepartmentName(delivery?.address ?? ''),
    value: delivery?.delivery_department ?? '',
  });
  const warehouses =
    isPickup &&
    delivery?.delivery === OrdersDeliveryType.PICKUP &&
    delivery?.delivery_warehouse_id &&
    delivery?.settlement_id === selectedSettlement
      ? getDepartments(warehousesOptions ?? [], {
          label: delivery.address ?? '',
          value: delivery.delivery_warehouse_id,
        })
      : warehousesOptions ?? [];

  const handleSettlementsChange = (_: string, option: Record<string, string>) => {
    setRegionId(option?.regionId || null);
    formInstance.setFieldsValue({
      [UpdateOrderDeliveryFormFieldsNames.DELIVERY_DEPARTMENT]: '',
      [UpdateOrderDeliveryFormFieldsNames.DELIVERY_WAREHOUSE]: '',
    });
  };

  const handleDeliveryTypeChange = () => {
    setRegionId(null);
    formInstance.setFieldsValue({
      [UpdateOrderDeliveryFormFieldsNames.SETTLEMENT]: undefined,
      [UpdateOrderDeliveryFormFieldsNames.DELIVERY_DEPARTMENT]: undefined,
      [UpdateOrderDeliveryFormFieldsNames.DELIVERY_WAREHOUSE]: undefined,
      [UpdateOrderDeliveryFormFieldsNames.ADDRESS]: undefined,
      [UpdateOrderDeliveryFormFieldsNames.BUILDING_TYPE]: undefined,
      [UpdateOrderDeliveryFormFieldsNames.FLOOR]: undefined,
      [UpdateOrderDeliveryFormFieldsNames.FLAT_NUMBER]: undefined,
      [UpdateOrderDeliveryFormFieldsNames.ELEVATOR]: undefined,
    });
  };

  const handleDepartmentsSearch = (searchValue: string) => {
    const settlementTo = selectedSettlement || delivery?.settlement_id;
    if (!settlementTo) {
      return;
    }

    onDepartmentsSearch(searchValue, delivery?.settlement_id || settlementTo, settlementTo);
  };

  useEffect(() => {
    if (!isPickup || isLoading || !selectedSettlement) {
      return;
    }

    const pickupOptions = settlementsOptions || [];
    if (pickupOptions.length === 0) {
      return;
    }

    const isValidPickupSettlement = pickupOptions.some((option) => option.value === selectedSettlement);

    if (!isValidPickupSettlement) {
      formInstance.setFieldsValue({
        [UpdateOrderDeliveryFormFieldsNames.SETTLEMENT]: undefined,
        [UpdateOrderDeliveryFormFieldsNames.DELIVERY_WAREHOUSE]: undefined,
      });
    }
  }, [isPickup, isLoading, selectedSettlement, settlementsOptions, formInstance]);

  const renderForm = useCallback(() => {
    switch (resolvedDeliveryType) {
      case OrdersDeliveryType.NOVA_POSHTA:
        return (
          <>
            <StyledFormItem
              label={t('orders_update_delivery_delivery_department_label')}
              name={UpdateOrderDeliveryFormFieldsNames.DELIVERY_DEPARTMENT}
              rules={validationRules[UpdateOrderDeliveryFormFieldsNames.DELIVERY_DEPARTMENT]}
            >
              <StyledSelect
                allowClear
                showSearch
                loading={isDepartmentsLoading}
                options={departments ?? []}
                onSearch={handleDepartmentsSearch}
              />
            </StyledFormItem>
            <StyledFormItem
              label={t('orders_update_delivery_comment_label')}
              name={UpdateOrderDeliveryFormFieldsNames.COMMENT}
              rules={validationRules[UpdateOrderDeliveryFormFieldsNames.COMMENT]}
            >
              <StyledTextArea rows={5} />
            </StyledFormItem>
          </>
        );
      case OrdersDeliveryType.PICKUP:
        return (
          <>
            <StyledFormItem
              label={t('orders_update_delivery_delivery_warehouse_label')}
              name={UpdateOrderDeliveryFormFieldsNames.DELIVERY_WAREHOUSE}
              rules={validationRules[UpdateOrderDeliveryFormFieldsNames.DELIVERY_WAREHOUSE]}
            >
              <StyledSelect
                allowClear
                showSearch
                loading={isWarehousesLoading}
                onSearch={(value) => onWarehousesSearch(value, selectedSettlement)}
                options={warehouses ?? []}
              />
            </StyledFormItem>
            <StyledFormItem
              label={t('orders_update_delivery_comment_label')}
              name={UpdateOrderDeliveryFormFieldsNames.COMMENT}
              rules={validationRules[UpdateOrderDeliveryFormFieldsNames.COMMENT]}
            >
              <StyledTextArea rows={5} />
            </StyledFormItem>
          </>
        );
      default:
        return (
          <>
            <MapsProvider>
              <StyledFormItem
                label={t('orders_update_delivery_address_label')}
                name={UpdateOrderDeliveryFormFieldsNames.ADDRESS}
                rules={validationRules[UpdateOrderDeliveryFormFieldsNames.ADDRESS]}
              >
                <PlaceAutocomplete />
              </StyledFormItem>
            </MapsProvider>
            <StyledFormItem
              label={t('orders_update_delivery_building_type_label')}
              name={UpdateOrderDeliveryFormFieldsNames.BUILDING_TYPE}
              rules={validationRules[UpdateOrderDeliveryFormFieldsNames.BUILDING_TYPE]}
            >
              <StyledSelect options={buildingTypeOptions(t)} />
            </StyledFormItem>

            {buildingType === BuildingTypes.FLAT && (
              <>
                <StyledFormItemWrap>
                  <StyledFormItem
                    label={t('orders_update_delivery_floor_label')}
                    name={UpdateOrderDeliveryFormFieldsNames.FLOOR}
                    rules={validationRules[UpdateOrderDeliveryFormFieldsNames.FLOOR]}
                  >
                    <StyledInputNumber />
                  </StyledFormItem>

                  <StyledFormItem
                    label={t('orders_update_delivery_flat_number_label')}
                    name={UpdateOrderDeliveryFormFieldsNames.FLAT_NUMBER}
                    rules={validationRules[UpdateOrderDeliveryFormFieldsNames.FLAT_NUMBER]}
                  >
                    <StyledInput />
                  </StyledFormItem>
                </StyledFormItemWrap>

                <StyledFormItem
                  label={t('orders_update_delivery_elevator_label')}
                  name={UpdateOrderDeliveryFormFieldsNames.ELEVATOR}
                  rules={validationRules[UpdateOrderDeliveryFormFieldsNames.ELEVATOR]}
                >
                  <StyledSelect options={elevatorOptions(t)} />
                </StyledFormItem>
              </>
            )}

            <StyledFormItem
              label={t('orders_update_delivery_comment_label')}
              name={UpdateOrderDeliveryFormFieldsNames.COMMENT}
              rules={validationRules[UpdateOrderDeliveryFormFieldsNames.COMMENT]}
            >
              <StyledTextArea rows={5} />
            </StyledFormItem>
          </>
        );
    }
  }, [
    resolvedDeliveryType,
    settlementsOptions,
    buildingType,
    order.delivery,
    order.id,
    departmentsOptions,
    warehouses,
  ]);

  useEffect(() => {
    if (resolvedDeliveryType === OrdersDeliveryType.NOVA_POSHTA && deliveryDepartment && departmentsOptions) {
      const departmentExists = departments.some((option) => option.value === deliveryDepartment);
      if (!departmentExists) {
        formInstance.setFieldsValue({
          [UpdateOrderDeliveryFormFieldsNames.DELIVERY_DEPARTMENT]: undefined,
        });
      }
    }
  }, [resolvedDeliveryType, deliveryDepartment, departmentsOptions, formInstance]);

  useEffect(() => {
    if (!isOpenUpdateOrderDeliveryModal) {
      return;
    }

    if (resolvedDeliveryType === OrdersDeliveryType.NOVA_POSHTA) {
      handleDepartmentsSearch('');
    }
  }, [isOpenUpdateOrderDeliveryModal, selectedSettlement, resolvedDeliveryType]);

  return (
    <Modal visible={isOpenUpdateOrderDeliveryModal} onCancel={closeUpdateOrderDeliveryModal} size={ModalSize.LARGE}>
      <StyledForm form={formInstance} onFinish={handleSubmit} initialValues={initialValues}>
        <StyledTitleModal>{t('orders_update_delivery_modal_title')}</StyledTitleModal>
        <StyledFormItem
          label={t('orders_update_delivery_type_label')}
          name={UpdateOrderDeliveryFormFieldsNames.DELIVERY_TYPE}
          rules={validationRules[UpdateOrderDeliveryFormFieldsNames.DELIVERY_TYPE]}
        >
          <StyledSelect
            options={getDeliveryTypeOptions(t)}
            defaultValue={OrdersDeliveryType.NOVA_POSHTA}
            onChange={handleDeliveryTypeChange}
          />
        </StyledFormItem>
        <StyledFormItem
          label={t('orders_update_delivery_settlement_label')}
          name={UpdateOrderDeliveryFormFieldsNames.SETTLEMENT}
          rules={validationRules[UpdateOrderDeliveryFormFieldsNames.SETTLEMENT]}
        >
          <StyledSelect
            allowClear
            showSearch
            filterOption={false}
            loading={isLoading}
            onSearch={onSearch}
            onChange={(value, option) =>
              handleSettlementsChange(value as string, option as unknown as Record<string, string>)
            }
            options={settlements ?? []}
          />
        </StyledFormItem>
        {renderForm()}
        <StyledButton htmlType="submit" disabled={isLoading} type="primary">
          {t('orders_update_delivery_modal_button')}
        </StyledButton>
      </StyledForm>
    </Modal>
  );
};

export default UpdateOrderDeliveryModal;
